import { useCallback } from 'react';
import { validate } from 'uuid';
import { useParams } from 'react-router';
import { useStore } from '30.quickConnect.Stores';
import { isListField } from '90.quickConnect.Models/guards';
import { Choice, EntityData, FieldDesc, RepeatableGroupDesc } from '90.quickConnect.Models/models';
import { FieldType } from '90.quickConnect.Models/enums';
import { flatten } from '80.quickConnect.Core/helpers';
import { MAX_HIERARCHICAL_PREFETCH } from '80.quickConnect.Core/const';
import { isApplicativeListId, LIST_ID_DOCUMENTS } from '80.quickConnect.Core/const/applicativeListIds';
import equipmentsDb from '40.quickConnect.DataAccess/indexedDb/dbs/equipmentsDb';
import { DateTimeExtension } from '80.quickConnect.Core/formatting/DateTimeExtension';

const useHydrateChoiceList = () => {
  const { entityInstanceId } = useParams();

  const {
    SharedListStore: { getSharedList },
  } = useStore();

  const getCurrentEquipment = useCallback(async (): Promise<EntityData | undefined> => {
    if (!entityInstanceId) return undefined;
    return equipmentsDb.getEquipmentById(entityInstanceId);
  }, [entityInstanceId]);

  const hasValidateListId = useCallback(
    (listId: string | undefined): listId is string => !!listId && (validate(listId) || isApplicativeListId(listId)),
    [],
  );

  const getPreviousDeclarationsList = useCallback(async (): Promise<Choice[]> => {
    const equipment = await getCurrentEquipment();
    if (!equipment?.historicalData?.length) return [];

    return [...equipment.historicalData]
      .sort((a, b) => {
        const left = a.editedAt ? new Date(a.editedAt).getTime() : 0;
        const right = b.editedAt ? new Date(b.editedAt).getTime() : 0;
        return right - left;
      })
      .map((historical) => {
        const formattedDate = historical.editedAt
          ? DateTimeExtension.formatShortDateAndTime(new Date(historical.editedAt))
          : '';
        const label = `${historical.stringExtract ?? ''} ${formattedDate}`.trim();
        return {
          value: historical.id,
          label,
        } as Choice;
      });
  }, [getCurrentEquipment]);

  const getEquipmentDocumentsList = useCallback(async (): Promise<Choice[]> => {
    const equipment = await getCurrentEquipment();
    if (!equipment?.qcDocuments?.length) return [];

    return equipment.qcDocuments.filter(Boolean).map((doc) => ({
      value: doc.documentId ?? doc.id,
      label: doc.name || doc.title || doc.documentId || doc.id,
      data: undefined,
    }));
  }, [getCurrentEquipment]);

  const hydrateChoiceList = useCallback(
    async (fields: FieldDesc[]): Promise<void> => {
      // On doit flatten aussi les items instanciés si RepeatableGroup avec nbRowOnInit > 0
      const allFields = flatten(fields, (f) => {
        if (f.fieldType === FieldType.RepeatableGroup) {
          const rg = f as RepeatableGroupDesc;
          const result: FieldDesc[] = [];

          // 1. Template (toujours)
          if (rg.groupTemplate?.length) {
            result.push(...rg.groupTemplate);
          }

          // 2. Items instanciés (si nbRowOnInit > 0)
          if ((rg.nbRowOnInit ?? 0) > 0 && Array.isArray(rg.items)) {
            rg.items.forEach((row) => {
              if (row.items?.length) {
                result.push(...row.items);
              }
            });
          }

          return result;
        }

        // Cas normal : descente simple
        return f.items;
      });

      await Promise.all(
        allFields.map(async (f) => {
          if (!isListField(f) || !hasValidateListId(f.listId) || f.listChoice !== undefined) return;

          const listId = f.listId.toLowerCase();

          if (isApplicativeListId(listId)) {
            if (listId === LIST_ID_DOCUMENTS) {
              f.listChoice = await getEquipmentDocumentsList();
              return;
            }

            f.listChoice = await getPreviousDeclarationsList();
            return;
          }

          const sharedList = await getSharedList(f.listId);
          if (!sharedList) return;

          if (f.fieldType === FieldType.HierarchicalList) {
            const hierarchicalChoicesLength = flatten(sharedList.hierarchicalChoices, (n) => n?.children ?? []).filter(
              (n) => !n?.children || n.children.length === 0,
            ).length;

            if (hierarchicalChoicesLength > MAX_HIERARCHICAL_PREFETCH) {
              // Très grosse liste hiérarchique -> on n’injecte PAS listChoice dans l’arbre
              return;
            }
            // Petite/medium hiérarchique -> on hydrate comme avant
            f.listChoice = sharedList.hierarchicalChoices;
          } else {
            // Listes non hiérarchiques -> comportement inchangé
            f.listChoice = sharedList.data;
          }
        }),
      );
    },
    [getEquipmentDocumentsList, getPreviousDeclarationsList, getSharedList, hasValidateListId],
  );

  return { hydrateChoiceList };
};

export default useHydrateChoiceList;
